import React, { Component } from 'react';
import classNames from 'classnames';
import debounce from 'lodash/debounce';

import { FormattedMessage, injectIntl } from '../../../util/reactIntl';
import { types as sdkTypes } from '../../../util/sdkLoader';
import { LocationAutocompleteInput, IconLocation, RangeSlider } from '../../../components';

import rangeCss from '../IntegerRangeFilter/IntegerRangeFilter.module.css';
import css from './PickupRadiusFilter.module.css';
import {
  buildLocationStateFromUrl,
  isCurrentLocationPlace,
  pickupAddressForSubmit,
} from './pickupRadiusFilterHelpers';

const { LatLng, LatLngBounds } = sdkTypes;

const EARTH_RADIUS_MILES = 3958.8;
const MIN_RADIUS = 1;
const MAX_RADIUS = 500;

const computeBoundsFromMiles = (lat, lng, radiusMiles) => {
  const dLat = (radiusMiles / EARTH_RADIUS_MILES) * (180 / Math.PI);
  const latRad = (lat * Math.PI) / 180;
  const dLng = (radiusMiles / (EARTH_RADIUS_MILES * Math.cos(latRad))) * (180 / Math.PI);
  return new LatLngBounds(new LatLng(lat + dLat, lng + dLng), new LatLng(lat - dLat, lng - dLng));
};

const DEFAULT_RADIUS = 50;
const RADIUS_SUBMIT_DEBOUNCE_MS = 400;

const LocationPinIcon = () => <IconLocation rootClassName={css.locationPinIcon} />;

/**
 * Location + radius controls nested under the pickup checkbox in DeliveryFilter.
 */
class PickupRadiusFilter extends Component {
  constructor(props) {
    super(props);

    const { urlQueryParams = {}, intl } = props;
    const currentLocationLabel = intl.formatMessage({
      id: 'LocationAutocompleteInput.currentLocation',
    });
    const urlState = buildLocationStateFromUrl(
      urlQueryParams,
      currentLocationLabel,
      DEFAULT_RADIUS
    );

    this.state = {
      ...urlState,
      isLocationInputActive: false,
    };

    this.blurTimer = null;

    this.handleLocationChange = this.handleLocationChange.bind(this);
    this.handleLocationFocus = this.handleLocationFocus.bind(this);
    this.handleLocationBlur = this.handleLocationBlur.bind(this);
    this.handleSliderChange = this.handleSliderChange.bind(this);
    this.handleClearLocation = this.handleClearLocation.bind(this);
    this.handleSelectionError = this.handleSelectionError.bind(this);
    this.submitRadiusSearch = this.submitRadiusSearch.bind(this);
    this.debouncedSubmitRadiusSearch = debounce(this.submitRadiusSearch, RADIUS_SUBMIT_DEBOUNCE_MS);
  }

  componentDidUpdate(prevProps) {
    const prev = prevProps.urlQueryParams || {};
    const next = this.props.urlQueryParams || {};

    const urlChanged =
      prev.pickupOrigin !== next.pickupOrigin ||
      prev.pickupAddress !== next.pickupAddress ||
      prev.pickupRadius !== next.pickupRadius;

    if (urlChanged) {
      const { intl } = this.props;
      const currentLocationLabel = intl.formatMessage({
        id: 'LocationAutocompleteInput.currentLocation',
      });
      this.setState(buildLocationStateFromUrl(next, currentLocationLabel, DEFAULT_RADIUS));
    }
  }

  componentWillUnmount() {
    if (this.blurTimer) {
      window.clearTimeout(this.blurTimer);
    }
    this.debouncedSubmitRadiusSearch.cancel();
  }

  handleLocationFocus() {
    if (this.blurTimer) {
      window.clearTimeout(this.blurTimer);
      this.blurTimer = null;
    }
    this.setState({ isLocationInputActive: true, locationError: null });
  }

  handleLocationBlur() {
    this.blurTimer = window.setTimeout(() => {
      this.setState({ isLocationInputActive: false });
    }, 200);
  }

  handleSelectionError(error, { isCurrentLocation } = {}) {
    if (!isCurrentLocation) {
      return;
    }

    const { intl } = this.props;
    this.setState({
      isCurrentLocation: false,
      locationError: intl.formatMessage({ id: 'PickupRadiusFilter.locationError' }),
    });
  }

  submitRadiusSearch(locationValue, radius) {
    const { onSubmit, inactive } = this.props;
    if (inactive) return;

    const selectedPlace = locationValue?.selectedPlace;
    if (!selectedPlace?.origin) return;

    const { lat, lng } = selectedPlace.origin;
    const bounds = computeBoundsFromMiles(lat, lng, radius);

    onSubmit({
      bounds,
      pickupAddress: pickupAddressForSubmit(selectedPlace, locationValue.search),
      pickupOrigin: `${lat},${lng}`,
      pickupRadius: String(radius),
    });
  }

  handleLocationChange(value) {
    const isCurrentLocation = isCurrentLocationPlace(value?.selectedPlace);

    this.setState(
      {
        locationValue: value,
        isCurrentLocation,
        locationError: value?.selectedPlace?.origin ? null : this.state.locationError,
      },
      () => {
        if (value?.selectedPlace?.origin) {
          this.submitRadiusSearch(value, this.state.radius);
        }
      }
    );
  }

  handleSliderChange(handles) {
    const radius = handles[0];
    this.setState({ radius }, () => {
      this.debouncedSubmitRadiusSearch(this.state.locationValue, radius);
    });
  }

  handleClearLocation() {
    const { onSubmit, inactive } = this.props;
    if (inactive) return;

    this.setState({
      locationValue: { search: '', predictions: [], selectedPlace: null },
      radius: DEFAULT_RADIUS,
      isCurrentLocation: false,
      locationError: null,
    });

    onSubmit({
      bounds: null,
      pickupAddress: null,
      pickupOrigin: null,
      pickupRadius: null,
    });
  }

  render() {
    const { id, intl, inactive } = this.props;
    const {
      locationValue,
      radius,
      isLocationInputActive,
      isCurrentLocation,
      locationError,
    } = this.state;
    const hasLocation = !!locationValue?.selectedPlace?.origin;
    const currentLocationLabel = intl.formatMessage({
      id: 'LocationAutocompleteInput.currentLocation',
    });
    const displayAddress = isCurrentLocation
      ? currentLocationLabel
      : locationValue?.selectedPlace?.address || locationValue?.search;
    const radiusAriaLabel = intl.formatMessage({ id: 'PickupRadiusFilter.radiusAriaLabel' });
    const placeholder = isCurrentLocation
      ? currentLocationLabel
      : intl.formatMessage({ id: 'PickupRadiusFilter.placeholder' });
    const locationErrorId = `${id}.locationError`;

    return (
      <div
        className={classNames(css.root, inactive && css.inactive)}
        aria-hidden={inactive || undefined}
      >
        <div className={classNames(css.panel, isLocationInputActive && css.panelLocationActive)}>
          <label className={css.fieldLabel} htmlFor={`${id}.location`}>
            <FormattedMessage id="PickupRadiusFilter.nearLocationLabel" />
          </label>

          <div
            className={classNames(
              css.locationField,
              isLocationInputActive && css.locationFieldActive
            )}
          >
            <LocationAutocompleteInput
              id={`${id}.location`}
              rootClassName={css.locationRoot}
              iconClassName={css.locationIconWrapper}
              inputClassName={classNames(
                css.locationInput,
                isCurrentLocation && css.locationInputCurrentLocation
              )}
              predictionsClassName={css.predictions}
              predictionsListClassName={css.predictionsList}
              predictionsMapProviderClassName={css.predictionsMapProvider}
              predictionsAttributionClassName={css.predictionsAttribution}
              replacePredictionsAttribution
              CustomIcon={LocationPinIcon}
              useDarkText
              input={{
                value: locationValue,
                onChange: this.handleLocationChange,
                onBlur: this.handleLocationBlur,
                onFocus: this.handleLocationFocus,
                name: 'pickupLocation',
              }}
              meta={{ touched: false, error: null, warning: null }}
              placeholder={placeholder}
              closeOnBlur
              disabled={inactive}
              onSelectionError={this.handleSelectionError}
              ariaDescribedBy={locationError ? locationErrorId : null}
            />
            {locationError ? (
              <p id={locationErrorId} className={css.locationError} role="alert">
                {locationError}
              </p>
            ) : null}
          </div>

          {hasLocation ? (
            <div className={css.selectedLocation}>
              <span className={css.selectedLocationText}>{displayAddress}</span>
              <button
                type="button"
                className={css.clearLocationButton}
                onClick={this.handleClearLocation}
                disabled={inactive}
              >
                <FormattedMessage id="PickupRadiusFilter.clearLocation" />
              </button>
            </div>
          ) : null}

          <div className={css.radiusSection}>
            <div className={rangeCss.rangeReadoutInline} aria-live="polite">
              <FormattedMessage id="PickupRadiusFilter.withinMiles" values={{ radius }} />
            </div>

            <div className={classNames(rangeCss.sliderWrapperCompact, css.radiusSliderWrapper)}>
              <RangeSlider
                min={MIN_RADIUS}
                max={MAX_RADIUS}
                step={1}
                handles={[radius]}
                ariaLabels={[radiusAriaLabel]}
                onChange={this.handleSliderChange}
              />
            </div>

            <div className={rangeCss.endpointLabels}>
              <span>1 mi</span>
              <span>500 mi</span>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default injectIntl(PickupRadiusFilter);
